import React, { useMemo, useState } from 'react';
import { Check, MoreHorizontal, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import type { ChatThreadDTO } from '../../global';
import { cn } from '../../components/ui/cn';
import { dateLocale, t } from '../../lib/i18n';

interface ChatThreadListProps {
  threads: ChatThreadDTO[];
  activeId: string | null;
  onNew: () => void;
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDeleteAll: () => Promise<void>;
  /** Telefon: daha büyük dokunma alanları. */
  touch?: boolean;
}

function dayLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const today = new Date();
  if (at.toDateString() === today.toDateString()) return t('Bugün', 'Today');
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (at.toDateString() === yesterday.toDateString()) return t('Dün', 'Yesterday');
  return at.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', ...(at.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
}

/**
 * Kayıtlı sohbetler: yeni sohbet, arama, yeniden adlandırma ve silme. Telefonda sol çekmecede
 * (MobileMeetingsDrawer), masaüstünde sohbet sayfasının solunda. Başlıklar sunucudan çözülmüş gelir.
 */
export const ChatThreadList: React.FC<ChatThreadListProps> = ({ threads, activeId, onNew, onOpen, onRename, onDelete, onDeleteAll, touch = false }) => {
  const [query, setQuery] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(dateLocale());
    return needle ? threads.filter((thread) => thread.title.toLocaleLowerCase(dateLocale()).includes(needle)) : threads;
  }, [threads, query]);

  const rowHeight = touch ? 'min-h-[52px]' : 'min-h-[38px]';
  const iconButton = cn(
    'shrink-0 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground active:bg-secondary',
    touch ? 'w-11 h-11' : 'w-8 h-8'
  );

  const saveEdit = async () => {
    if (!editing) return;
    const title = editing.title.trim();
    setEditing(null);
    if (title) await onRename(editing.id, title);
  };

  const confirmDelete = async (thread: ChatThreadDTO) => {
    setMenuFor(null);
    if (!window.confirm(t(`“${thread.title}” sohbeti kalıcı olarak silinsin mi?`, `Permanently delete the chat “${thread.title}”?`))) return;
    setBusy(true);
    await onDelete(thread.id);
    setBusy(false);
  };

  const confirmDeleteAll = async () => {
    if (!window.confirm(t('Bütün sohbetler kalıcı olarak silinsin mi? Bu geri alınamaz.', 'Permanently delete all chats? This can’t be undone.'))) return;
    setBusy(true);
    await onDeleteAll();
    setBusy(false);
  };

  return (
    <div className="h-full min-h-0 flex flex-col gap-3">
      <button
        type="button"
        onClick={onNew}
        className={cn(
          'mx-1 flex items-center gap-2.5 px-3 rounded-xl bg-secondary text-foreground font-semibold active:opacity-80 hover:bg-secondary/80 transition-colors',
          touch ? 'h-12 text-[16px]' : 'h-10 text-[13.5px]'
        )}
      >
        <Plus className={touch ? 'w-5 h-5' : 'w-4 h-4'} />
        {t('Yeni sohbet', 'New chat')}
      </button>

      {threads.length > 4 && (
        <label className={cn('mx-1 flex items-center gap-2.5 px-3 rounded-xl bg-secondary text-muted-foreground', touch ? 'h-11' : 'h-9')}>
          <Search className="w-4 h-4 shrink-0" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Sohbet ara', 'Search chats')}
            aria-label={t('Sohbet ara', 'Search chats')}
            className={cn('flex-1 min-w-0 bg-transparent border-0 outline-none text-foreground placeholder:text-muted-foreground', touch ? 'text-[16px]' : 'text-[13px]')}
          />
        </label>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-0.5">
        {threads.length === 0 ? (
          <p className={cn('px-3 py-2 italic text-muted-foreground', touch ? 'text-[14.5px]' : 'text-[12.5px]')}>
            {t('Henüz kayıtlı sohbet yok. Sorduklarını burada bulursun.', 'No saved chats yet. Your conversations will show up here.')}
          </p>
        ) : filtered.length === 0 ? (
          <p className={cn('px-3 py-2 italic text-muted-foreground', touch ? 'text-[14.5px]' : 'text-[12.5px]')}>{t('Eşleşen sohbet yok', 'No matching chats')}</p>
        ) : (
          filtered.map((thread) => {
            const isActive = thread.id === activeId;
            const isEditing = editing?.id === thread.id;
            return (
              <div key={thread.id}>
                <div className={cn('flex items-center rounded-xl', rowHeight, isActive ? 'bg-secondary' : 'hover:bg-secondary/60')}>
                  {isEditing ? (
                    <form
                      className="flex-1 min-w-0 flex items-center gap-1 pl-2 pr-1"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveEdit();
                      }}
                    >
                      <input
                        autoFocus
                        value={editing.title}
                        maxLength={80}
                        onChange={(event) => setEditing({ id: thread.id, title: event.target.value })}
                        onKeyDown={(event) => event.key === 'Escape' && setEditing(null)}
                        aria-label={t('Sohbet adı', 'Chat name')}
                        className={cn('flex-1 min-w-0 h-9 px-2 rounded-lg bg-background border border-border outline-none text-foreground', touch ? 'text-[16px]' : 'text-[13px]')}
                      />
                      <button type="submit" aria-label={t('Kaydet', 'Save')} className={iconButton}>
                        <Check className="w-4 h-4" />
                      </button>
                      <button type="button" aria-label={t('Vazgeç', 'Cancel')} onClick={() => setEditing(null)} className={iconButton}>
                        <X className="w-4 h-4" />
                      </button>
                    </form>
                  ) : (
                    <>
                      <button type="button" onClick={() => onOpen(thread.id)} className="flex-1 min-w-0 flex items-center gap-2 pl-3 py-2 text-left">
                        <span className={cn('flex-1 min-w-0 truncate text-foreground', touch ? 'text-[15.5px]' : 'text-[13px]', isActive && 'font-semibold')}>{thread.title}</span>
                        <span className={cn('shrink-0 text-muted-foreground whitespace-nowrap', touch ? 'text-[12.5px]' : 'text-[11px]')}>{dayLabel(thread.lastMessageAt)}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={t('Sohbet seçenekleri', 'Chat options')}
                        aria-expanded={menuFor === thread.id}
                        onClick={() => setMenuFor(menuFor === thread.id ? null : thread.id)}
                        className={iconButton}
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
                {menuFor === thread.id && !isEditing && (
                  <div className="ml-3 mt-0.5 mb-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuFor(null);
                        setEditing({ id: thread.id, title: thread.title });
                      }}
                      className={cn('flex items-center gap-1.5 px-3 rounded-lg text-foreground hover:bg-secondary active:bg-secondary', touch ? 'h-11 text-[14.5px]' : 'h-8 text-[12.5px]')}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {t('Yeniden adlandır', 'Rename')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void confirmDelete(thread)}
                      className={cn('flex items-center gap-1.5 px-3 rounded-lg text-rose-500 hover:bg-rose-500/10 active:bg-rose-500/10 disabled:opacity-50', touch ? 'h-11 text-[14.5px]' : 'h-8 text-[12.5px]')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('Sil', 'Delete')}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="mx-1 space-y-1.5 shrink-0">
        <p className={cn('text-muted-foreground leading-snug', touch ? 'text-[12.5px]' : 'text-[11px]')}>
          {t('Sohbetler şifreli saklanır ve son mesajdan 12 ay sonra silinir.', 'Chats are stored encrypted and deleted 12 months after the last message.')}
        </p>
        {threads.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void confirmDeleteAll()}
            className={cn('text-rose-500 hover:text-rose-400 disabled:opacity-50 font-medium', touch ? 'h-11 text-[14px]' : 'h-8 text-[12px]')}
          >
            {t('Tüm sohbetleri sil', 'Delete all chats')}
          </button>
        )}
      </div>
    </div>
  );
};
